import {
  Image,
  MessageSquare,
  MessagesSquare,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { ChannelName, MetaOAuthChannel, ProviderName } from '@/types/integrations';

/**
 * Catálogo de Integraciones: qué canales existen, qué credenciales pide cada uno y qué
 * proveedores de IA se configuran.
 *
 * Es un espejo del backend REAL (`backend/src/modules/integrations/integrations.schemas.ts`),
 * que valida las credenciales por canal al guardarlas y al probarlas:
 *   - whatsapp → phone_number_id, waba_id, access_token, verify_token
 *   - instagram → page_id, access_token, ig_business_id (opcional), verify_token
 *   - messenger → page_id, access_token, verify_token
 *
 * TikTok (`tiktok` → JSON libre) sigue vivo en el backend pero **no** se ofrece en el panel: ver la
 * nota al final de `CHANNEL_SPECS`.
 *
 * Los campos `secret` se pintan con `PasswordInput` (los tokens no se muestran en claro
 * mientras se escriben).
 */

export interface ChannelFieldSpec {
  key: string;
  label: string;
  hint?: string;
  /** Se enmascara en el formulario (tokens). */
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
}

export interface ChannelSpec {
  channel: ChannelName;
  label: string;
  /** Una línea que explica para qué sirve el canal. */
  description: string;
  icon: LucideIcon;
  fields: ChannelFieldSpec[];
  /** Canal que además se puede conectar con Facebook Login (onboarding OAuth). */
  oauthChannel?: MetaOAuthChannel;
  /** Sus credenciales son JSON libre (pares clave/valor) en vez de campos fijos. */
  freeFormCredentials?: boolean;
  /** Aviso propio del canal. */
  note?: string;
}

const VERIFY_TOKEN_FIELD: ChannelFieldSpec = {
  key: 'verify_token',
  label: 'verify_token',
  secret: true,
  required: true,
  hint: 'El mismo valor que configures como Verify Token del webhook en la app de Meta.',
  placeholder: 'token secreto del webhook',
};

/** Los cuatro canales, en el orden en que los muestra la pantalla. */
export const CHANNEL_SPECS: ChannelSpec[] = [
  {
    channel: 'whatsapp',
    label: 'WhatsApp',
    description: 'El canal principal: el bot atiende, responde y usa las plantillas aprobadas.',
    icon: MessageSquare,
    fields: [
      {
        key: 'phone_number_id',
        label: 'phone_number_id',
        required: true,
        hint: 'ID del número en Meta (WhatsApp → API Setup).',
        placeholder: '123456789012345',
      },
      {
        key: 'waba_id',
        label: 'waba_id',
        required: true,
        hint: 'ID de la cuenta de WhatsApp Business: lo necesitan las plantillas para crear y enviar.',
        placeholder: '987654321098765',
      },
      {
        key: 'access_token',
        label: 'access_token',
        secret: true,
        required: true,
        hint: 'Token permanente del número (System User si vas a producción).',
        placeholder: 'EAAG…',
      },
      VERIFY_TOKEN_FIELD,
    ],
  },
  {
    channel: 'messenger',
    label: 'Messenger',
    description: 'Mensajes de la página de Facebook del negocio.',
    icon: MessagesSquare,
    oauthChannel: 'messenger',
    fields: [
      {
        key: 'page_id',
        label: 'page_id',
        required: true,
        hint: 'ID de la página de Facebook.',
        placeholder: '1029384756',
      },
      {
        key: 'access_token',
        label: 'access_token',
        secret: true,
        required: true,
        hint: 'Page access token con permiso de mensajería.',
        placeholder: 'EAAG…',
      },
      VERIFY_TOKEN_FIELD,
    ],
  },
  {
    channel: 'instagram',
    label: 'Instagram',
    description: 'Mensajes directos de la cuenta de Instagram profesional.',
    icon: Image,
    oauthChannel: 'instagram',
    fields: [
      {
        key: 'page_id',
        label: 'page_id',
        required: true,
        hint: 'ID de la página de Facebook vinculada a la cuenta de Instagram.',
        placeholder: '1029384756',
      },
      {
        key: 'access_token',
        label: 'access_token',
        secret: true,
        required: true,
        hint: 'Page access token con permisos de Instagram.',
        placeholder: 'EAAG…',
      },
      {
        key: 'ig_business_id',
        label: 'ig_business_id',
        hint: 'Opcional: lo completa solo el onboarding con Facebook.',
        placeholder: '17841400000000000',
      },
      VERIFY_TOKEN_FIELD,
    ],
  },
];

/*
 * TikTok se sacó del panel (20-sep-2026): no se va a usar por ahora. El **backend sigue igual** —
 * acepta el canal `tiktok` con credenciales JSON libres, las cifra y su `test` responde sin salir a
 * la red (`integrations.schemas.ts` / `integrations.service.ts`) —, así que el día que se integre su
 * API solo hay que volver a añadir su spec aquí (con `freeFormCredentials: true`, el camino que ya
 * soporta `ChannelCredentialsDialog`). Los tipos (`ChannelName`, `Channel`) y las etiquetas
 * (`channelMeta`, `channelLabel`) sí lo conservan para no romper con datos del backend.
 */

/** Spec de un canal (los resúmenes de la API vienen como lista y hay que cruzarlos). */
export function channelSpec(channel: ChannelName): ChannelSpec {
  const spec = CHANNEL_SPECS.find((item) => item.channel === channel);
  if (!spec) throw new Error(`Canal desconocido: ${channel}`);
  return spec;
}

/* ---------------------------------------------------------------------------
 * Proveedores de IA
 * ------------------------------------------------------------------------- */

export interface ProviderSpec {
  provider: ProviderName;
  label: string;
  /** Para qué lo usa el sistema. */
  description: string;
  icon: LucideIcon;
  placeholder: string;
  /** Dónde se genera la key. */
  docsUrl: string;
}

/**
 * DeepSeek es el cerebro conversacional; Gemini se usa SOLO para visión (identificar a qué
 * producto del catálogo corresponde una foto). Ver `docs/MODULOS.md` §2 y §4.
 */
export const PROVIDER_SPECS: ProviderSpec[] = [
  {
    provider: 'deepseek',
    label: 'DeepSeek',
    description:
      'Cerebro conversacional de los dos asistentes (ventas y postventa): sin esta key el bot no responde.',
    icon: Sparkles,
    placeholder: 'sk-…',
    docsUrl: 'https://platform.deepseek.com/api_keys',
  },
  {
    provider: 'gemini',
    label: 'Gemini',
    description:
      'Solo para visión: identificar a qué producto del catálogo corresponde la foto que manda un cliente.',
    icon: Image,
    placeholder: 'AIza…',
    docsUrl: 'https://aistudio.google.com/app/apikey',
  },
];

